import React, { useState, useEffect, useRef } from "react";
import "./TravelInsurance.css";
import AOS from "aos";
import "aos/dist/aos.css";
import TestimonialsSection from "../../common/testimonials-section/TestimonialsSection";
import ReCAPTCHA from "react-google-recaptcha";
import { useDispatch, useSelector } from "react-redux";
import {
  submitEnquiryForm,
  resetEnquiryFormState,
} from "../../store/slices/enquiryFormSlice";

const TravelInsurance = () => {
  const [tripType, setTripType] = useState("single");
  const [travellers, setTravellers] = useState(1);
  const [destination, setDestination] = useState("");
  const [leavingDate, setLeavingDate] = useState("");
  const [returnDate, setReturnDate] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [globalError, setGlobalError] = useState("");
  const [email, setEmail] = useState("");
  const recaptchaRef = useRef();
  const [captchaValue, setCaptchaValue] = useState(null);

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      offset: 100,
    });
  }, []);

  // Validation function
  const validateForm = () => {
    const newErrors = {};

    if (!destination.trim()) {
      newErrors.destination = "Destination is required";
    }

    if (!leavingDate) {
      newErrors.leavingDate = "Leaving date is required";
    }

    if (!returnDate) {
      newErrors.returnDate = "Return date is required";
    } else if (
      leavingDate &&
      returnDate &&
      new Date(returnDate) <= new Date(leavingDate)
    ) {
      newErrors.returnDate = "Return date must be after leaving date";
    }

    if (!mobileNumber.trim()) {
      newErrors.mobileNumber = "Mobile number is required";
    } else if (!/^[6-9]\d{9}$/.test(mobileNumber)) {
      newErrors.mobileNumber =
        "Please enter a valid 10-digit Indian mobile number";
    }

    if (!agreeTerms) {
      newErrors.agreeTerms = "You must agree to the terms and conditions";
    }

    if (!captchaValue) {
      newErrors.captcha = "Please complete the reCAPTCHA verification";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleTravellerSelect = (value) => {
    setTravellers(value);
    if (errors.travellers) {
      setErrors((prev) => ({ ...prev, travellers: "" }));
    }
  };

  const dispatch = useDispatch();
  const { success, error } = useSelector((state) => state.enquiryForm);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setGlobalError("");

    const payload = {
      fullname: "Travel Insurance Customer",
      email: email,
      mobile: mobileNumber,
      require_service: "Travel Insurance",
      tripType: tripType,
      destination: destination,
      leavingDate: leavingDate,
      returnDate: returnDate,
      numberOfTravellers: travellers,
      source: "Travel Insurance Page",
      inquiryType: "Travel Insurance Quote",
      agreeToTerms: agreeTerms,
      recaptchaToken: captchaValue,
    };

    dispatch(submitEnquiryForm(payload));
  };
  useEffect(() => {
    if (success) {
      setSubmitSuccess(true);

      setDestination("");
      setLeavingDate("");
      setReturnDate("");
      setMobileNumber("");
      setAgreeTerms(false);
      setTravellers(1);
      setTripType("single");

      setCaptchaValue(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }

      setErrors({});

      setTimeout(() => {
        setSubmitSuccess(false);
        dispatch(resetEnquiryFormState());
      }, 5000);

      setIsSubmitting(false);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      setGlobalError(error);

      setCaptchaValue(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }

      setIsSubmitting(false);
    }
  }, [error]);

  const handleInputChange = (field, value) => {
    switch (field) {
      case "destination":
        setDestination(value);
        break;
      case "leavingDate":
        setLeavingDate(value);
        break;
      case "returnDate":
        setReturnDate(value);
        break;
      case "mobileNumber":
        setMobileNumber(value);
        break;
      case "tripType":
        setTripType(value);
        break;
      case "agreeTerms":
        setAgreeTerms(value);
        break;
      case "email":
        setEmail(value);
        break;
      default:
        break;
    }
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
    if (errors.captcha) {
      setErrors((prev) => ({ ...prev, captcha: "" }));
    }
  };

  const coverageData = [
    {
      id: 1,
      title: "Double Secure",
      description:
        "This plan offers coverage for the most common mishaps that can be encountered on an international trip.",
      ringType: "partial-ring",
      icon: "🚶🧳",
    },
    {
      id: 2,
      title: "Double Secure Plus",
      description:
        "This plan not only covers you for the most common mishaps on your trip but also comes with an extra layer of protection that keeps you protected from each hiccup, so your trip goes just how it should!",
      ringType: "full-ring",
      icon: "🚶🧳",
    },
  ];

  const buySteps = [
    {
      title: "Enter Your Trip Details",
      desc: "Hop onto the KFS app or website, select your destination, number of travellers, and date of birth. Then, hit 'View Prices'.",
    },
    {
      title: "Choose Your Plan",
      desc: "Now, you can view the plans, select the one that is perfect for you, set your sum insured, and click 'Continue'.",
    },
    {
      title: "Fill, Confirm & Pay",
      desc: "Next, fill in your personal and nominee details, complete the health declaration, click 'Pay Now' and complete the payment.",
    },
    {
      title: "Done & Delivered!",
      desc: "That's it! Your policy arrives via email, SMS, and WhatsApp. Need it later? It's always on the KFS App!",
    },
  ];

  const fileSteps = [
    {
      title: "Step 1",
      desc: "Give us a call at 9909002220 (if in India) or drop a missed call at +91-9909002220 & we'll call back in 10 mins.",
    },
    {
      title: "Step 2",
      desc: "Upload the required documents & your bank account details, on the link sent.",
    },
    {
      title: "Step 3",
      desc: "We'll take care of the rest!",
    },
  ];

  const TravelVisa = () => {
    return (
      <div className="kfs-visa-container kfs-ti-container" data-aos="fade-up">
        <h1 className="kfs-main-title">
          List of Countries Where Indians Don't Need a Visa/Get a Visa on
          Arrival
        </h1>
        <p className="kfs-intro-text">
          We all know how boring visa applications and processes can be.
          Luckily, there are at least a few countries in the world where Indians
          don't need to go through the hassle of applying for one. Check the
          list of
          <span className="kfs-highlight">
            {" "}
            visa-free countries for Indians{" "}
          </span>
          and
          <span className="kfs-highlight"> visa-on-arrival countries</span>.
        </p>

        <h2 className="kfs-section-title" data-aos="fade-right">
          Visa-Free Countries for Indians
        </h2>
        <table className="kfs-visa-table" data-aos="fade-up">
          <thead>
            <tr>
              <th>Continent</th>
              <th>Countries</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Oceania</td>
              <td>Fiji, Vanuatu, Kiribati, Micronesia, Cook Islands, Niue</td>
            </tr>
            <tr>
              <td>Africa</td>
              <td>
                Senegal, Mauritius, Tunisia, Angola, Rwanda, Seychelles, Gambia
              </td>
            </tr>
            <tr>
              <td>Asia</td>
              <td>
                Nepal, Thailand, Malaysia, Sri Lanka, Bhutan, Hong Kong, Iran,
                Kazakhstan, Oman, Macao (SAR China), Palestinian Territories
              </td>
            </tr>
            <tr>
              <td>North America</td>
              <td>
                El Salvador, Jamaica, Barbados, Haiti, Dominica, Grenada,
                British Virgin Islands, Montserrat, St. Vincent and the
                Grenadines, St. Kitts and Nevis, Trinidad and Tobago
              </td>
            </tr>
          </tbody>
        </table>

        <h2 className="kfs-section-title" data-aos="fade-right">
          Visa on Arrival Countries for Indians
        </h2>
        <table className="kfs-visa-table" data-aos="fade-up">
          <thead>
            <tr>
              <th>Continent</th>
              <th>Countries</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>North America</td>
              <td>Antigua and Barbuda, Saint Lucia</td>
            </tr>
            <tr>
              <td>South America</td>
              <td>Bolivia, Suriname</td>
            </tr>
            <tr>
              <td>Oceania</td>
              <td>Marshall Islands, Palau Islands, Samoa, Tuvalu</td>
            </tr>
            <tr>
              <td>Asia</td>
              <td>
                Thailand, Maldives, Indonesia, Sri Lanka, Vietnam, Myanmar,
                Jordan, Iran, Cambodia, Bahrain, Laos, Tajikistan, Timor-Leste
              </td>
            </tr>
            <tr>
              <td>Africa</td>
              <td>
                Zimbabwe, Rwanda, Ethiopia, Angola, Madagascar, Mozambique, Cape
                Verde Islands, Burundi, Comoro Islands, Mauritania, Sierra
                Leone, Somalia, Tanzania, Guinea-Bissau, Gabon, Tunisia
              </td>
            </tr>
          </tbody>
        </table>

        <h2 className="kfs-section-title" data-aos="fade-right">
          Precautions to Remember Before Travelling Abroad
        </h2>
        <ul className="kfs-precautions" data-aos="fade-up">
          <li>
            Avoid taking your passport everywhere. Leave it in the hotel locker
            and carry a copy for ID purposes.
          </li>
          <li>
            Don't carry too much cash around. When withdrawing cash from an ATM,
            check surroundings and verify notes.
          </li>
          <li>
            Don't fall for travel scams like fake monks, overcharging cab
            drivers, fake tour guides, etc.
          </li>
          <li>
            Always keep your cash in more than one place – wallet and backpack.
          </li>
          <li>
            Remember your accommodation location and always use meter taxis.
          </li>
        </ul>
      </div>
    );
  };

  return (
    <div className="kfs-ti-page-wrapper">
      {/* HERO SECTION */}
      <section className="kfs-ti-hero-section">
        <div className="kfs-ti-hero-container">
          <div className="kfs-ti-hero-left" data-aos="fade-right">
            <h1>Travel Insurance</h1>
            <div className="kfs-ti-hero-graphic-placeholder">
              <img src="./images/insurance/travel1.png" alt="Travel Hero" />
            </div>
            <div className="kfs-ti-features-row">
              <div
                className="kfs-ti-feature-box"
                data-aos="zoom-in"
                data-aos-delay="100"
              >
                <span className="kfs-icon">📞</span>
                <div>
                  <strong>24X7</strong>
                  <p>Missed Call Facility</p>
                </div>
              </div>
              <div
                className="kfs-ti-feature-box"
                data-aos="zoom-in"
                data-aos-delay="200"
              >
                <span className="kfs-icon">₹</span>
                <div>
                  <strong>Affordable</strong>
                  <p>Premium</p>
                </div>
              </div>
              <div
                className="kfs-ti-feature-box"
                data-aos="zoom-in"
                data-aos-delay="300"
              >
                <span className="kfs-icon">🚣</span>
                <div>
                  <strong>1-Day Adventure</strong>
                  <p>Activities Covered</p>
                </div>
              </div>
            </div>
          </div>

          <div
            className="kfs-ti-hero-right"
            data-aos="fade-left"
            data-aos-delay="200"
          >
            <div className="kfs-ti-quote-card">
              {submitSuccess && (
                <div
                  className="kfs-ti-success-message"
                  style={{
                    backgroundColor: "#d4edda",
                    color: "#155724",
                    padding: "12px",
                    borderRadius: "4px",
                    marginBottom: "20px",
                    textAlign: "center",
                  }}
                >
                  <strong>Success!</strong> Your travel insurance inquiry has
                  been submitted successfully. We'll contact you soon.
                </div>
              )}

              {globalError && (
                <div
                  className="kfs-ti-error-message"
                  style={{
                    backgroundColor: "#f8d7da",
                    color: "#721c24",
                    padding: "12px",
                    borderRadius: "4px",
                    marginBottom: "20px",
                    textAlign: "center",
                  }}
                >
                  <strong>Error!</strong> {globalError}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="kfs-ti-form-group-radio">
                  <label className="kfs-ti-form-label">
                    Select Trip Type *
                  </label>
                  <div className="kfs-ti-radio-grid">
                    <label className="kfs-ti-icon-radio-label">
                      <input
                        type="radio"
                        name="tripType"
                        className="kfs-ti-radio-input"
                        checked={tripType === "single"}
                        onChange={() => handleInputChange("tripType", "single")}
                        disabled={isSubmitting}
                      />
                      <div className="kfs-ti-icon-radio-content">
                        <img
                          src="/images/insurance/icon1.png"
                          alt="Single Trip"
                          className="kfs-ti-radio-icon-png"
                        />
                        <span className="kfs-ti-radio-text">Single Trip</span>
                      </div>
                    </label>

                    <label className="kfs-ti-icon-radio-label">
                      <input
                        type="radio"
                        name="tripType"
                        className="kfs-ti-radio-input"
                        checked={tripType === "multi"}
                        onChange={() => handleInputChange("tripType", "multi")}
                        disabled={isSubmitting}
                      />
                      <div className="kfs-ti-icon-radio-content">
                        <img
                          src="/images/insurance/icon2.png"
                          alt="Multi Trip"
                          className="kfs-ti-radio-icon-png"
                        />
                        <span className="kfs-ti-radio-text">Multi Trip</span>
                      </div>
                    </label>

                    <label className="kfs-ti-icon-radio-label">
                      <input
                        type="radio"
                        name="tripType"
                        className="kfs-ti-radio-input"
                        checked={tripType === "student"}
                        onChange={() =>
                          handleInputChange("tripType", "student")
                        }
                        disabled={isSubmitting}
                      />
                      <div className="kfs-ti-icon-radio-content">
                        <img
                          src="/images/insurance/icon3.png"
                          alt="Student Trip"
                          className="kfs-ti-radio-icon-png"
                        />
                        <span className="kfs-ti-radio-text">Student</span>
                      </div>
                    </label>

                    <label className="kfs-ti-icon-radio-label">
                      <input
                        type="radio"
                        name="tripType"
                        className="kfs-ti-radio-input"
                        checked={tripType === "family"}
                        onChange={() => handleInputChange("tripType", "family")}
                        disabled={isSubmitting}
                      />
                      <div className="kfs-ti-icon-radio-content">
                        <img
                          src="/images/insurance/icon4.png"
                          alt="Family Trip"
                          className="kfs-ti-radio-icon-png"
                        />
                        <span className="kfs-ti-radio-text">Family</span>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="kfs-ti-form-group">
                  <label>Select Destination of Travel *</label>
                  <input
                    type="text"
                    placeholder="Search country"
                    value={destination}
                    onChange={(e) =>
                      handleInputChange("destination", e.target.value)
                    }
                    disabled={isSubmitting}
                  />
                  {errors.destination && (
                    <span
                      className="kfs-ti-error-text"
                      style={{
                        color: "#dc3545",
                        fontSize: "12px",
                        marginTop: "5px",
                        display: "block",
                      }}
                    >
                      {errors.destination}
                    </span>
                  )}
                </div>

                <div className="kfs-ti-form-row">
                  <div className="kfs-ti-form-group">
                    <label>Leaving date *</label>
                    <input
                      type="date"
                      value={leavingDate}
                      onChange={(e) =>
                        handleInputChange("leavingDate", e.target.value)
                      }
                      disabled={isSubmitting}
                    />
                    {errors.leavingDate && (
                      <span
                        className="kfs-ti-error-text"
                        style={{
                          color: "#dc3545",
                          fontSize: "12px",
                          marginTop: "5px",
                          display: "block",
                        }}
                      >
                        {errors.leavingDate}
                      </span>
                    )}
                  </div>
                  <div className="kfs-ti-form-group">
                    <label>Return date *</label>
                    <input
                      type="date"
                      value={returnDate}
                      onChange={(e) =>
                        handleInputChange("returnDate", e.target.value)
                      }
                      disabled={isSubmitting}
                    />
                    {errors.returnDate && (
                      <span
                        className="kfs-ti-error-text"
                        style={{
                          color: "#dc3545",
                          fontSize: "12px",
                          marginTop: "5px",
                          display: "block",
                        }}
                      >
                        {errors.returnDate}
                      </span>
                    )}
                  </div>
                </div>

                <div className="kfs-ti-form-group">
                  <label>Mobile Number *</label>
                  <div className="kfs-ti-phone-input">
                    <span>+91</span>
                    <input
                      type="tel"
                      placeholder="Enter Mobile Number"
                      value={mobileNumber}
                      onChange={(e) =>
                        handleInputChange("mobileNumber", e.target.value)
                      }
                      disabled={isSubmitting}
                      maxLength="10"
                    />
                  </div>
                  {errors.mobileNumber && (
                    <span
                      className="kfs-ti-error-text"
                      style={{
                        color: "#dc3545",
                        fontSize: "12px",
                        marginTop: "5px",
                        display: "block",
                      }}
                    >
                      {errors.mobileNumber}
                    </span>
                  )}
                </div>

                <div className="kfs-ti-form-group">
                  <label>Email Address *</label>
                  <input
                    type="email"
                    placeholder="Enter Email Address"
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    name="email"
                    disabled={isSubmitting}
                  />
                </div>

                <div className="kfs-ti-form-group">
                  <label>Select Number of Travellers</label>
                  <div className="kfs-ti-traveller-pills">
                    {[1, 2, 3, 4, "5 or more"].map((num) => (
                      <button
                        key={num}
                        className={`kfs-ti-pill ${travellers === num ? "active" : ""}`}
                        onClick={() => handleTravellerSelect(num)}
                        type="button"
                        disabled={isSubmitting}
                      >
                        {num}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="li-terms-group">
                  <input
                    type="checkbox"
                    id="ti-terms"
                    checked={agreeTerms}
                    onChange={(e) =>
                      handleInputChange("agreeTerms", e.target.checked)
                    }
                    disabled={isSubmitting}
                  />
                  <label htmlFor="ti-terms">
                    I agree to receive loan updates and promotional from{" "}
                    <strong>Krishana Finconcept</strong> via WhatsApp and other
                    accept the{" "}
                    <a
                      href="/privacy-security-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms & Conditions
                    </a>
                  </label>
                </div>

                {errors.agreeTerms && (
                  <span
                    className="kfs-ti-error-text"
                    style={{
                      color: "#dc3545",
                      fontSize: "12px",
                      marginTop: "5px",
                      display: "block",
                    }}
                  >
                    {errors.agreeTerms}
                  </span>
                )}

                <div
                  className="kfs-ti-recaptcha-box"
                  style={{ marginTop: "15px", marginBottom: "15px" }}
                >
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={
                      process.env.REACT_APP_GOOGLE_RECAPTCHA ||
                      "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"
                    }
                    onChange={handleCaptchaChange}
                  />
                  {errors.captcha && (
                    <span
                      className="kfs-ti-error-text"
                      style={{
                        color: "#dc3545",
                        fontSize: "12px",
                        marginTop: "5px",
                        display: "block",
                      }}
                    >
                      {errors.captcha}
                    </span>
                  )}
                </div>

                <button
                  type="submit"
                  className="kfs-ti-btn-primary"
                  disabled={isSubmitting}
                  style={{
                    opacity: isSubmitting ? 0.7 : 1,
                    cursor: isSubmitting ? "not-allowed" : "pointer",
                  }}
                >
                  {isSubmitting ? "Submitting..." : "View Prices"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO TEXT SECTIONS */}
      <section
        className="kfs-ti-text-section kfs-ti-centered"
        data-aos="fade-up"
      >
        <h2>
          International Travel Insurance Policy Online for your Overseas Travel
        </h2>
        <p>
          International travel insurance is a financial protection against any
          unforeseen losses or damages that can occur during your foreign trip.
          It covers your medical emergencies, trip disruptions, loss of baggage
          or passport, and personal liability abroad. Plus, it provides 24/7
          assistance, ensuring global travel is stress-free and financially
          secure.
        </p>
      </section>

      <section className="kfs-ti-text-section" data-aos="fade-up">
        <h2 className="kfs-ti-centered">
          What is International Travel Insurance?
        </h2>
        <p>
          International travel insurance, also known as overseas or foreign
          travel insurance, is a financial coverage designed for tourists,
          students, business travellers, and anyone planning to travel abroad
          from India. It provides comprehensive protection against unexpected
          events like medical emergencies, flight cancellations, lost baggage or
          passport, and personal liability.
        </p>
        <p>
          You should purchase it before your journey begins to ensure you're
          covered from the moment you travel. While it can't prevent travel
          hiccups, it helps you manage them confidently with 24/7 assistance and
          financial support, no matter where in the world you are.
        </p>
        <p>
          KFS International Travel Insurance offers simple, flexible plans that
          cover everything from medical emergencies to passport loss, from trip
          cancellations to even adventure activities, all with easy digital
          processes. With quick claim support and zero paperwork, it's built to
          help you travel smarter, safer, and stress-free.
        </p>
      </section>

      <section
        className="kfs-ti-text-section kfs-ti-centered"
        data-aos="fade-up"
      >
        <h2>Is Travel Insurance Mandatory for International Travel?</h2>
        <p>
          Yes, travel insurance is mandatory for visiting certain countries from
          India, such as those in the Schengen zone. While not all countries
          require it, having international travel insurance is highly
          recommended; it not only protects you from unexpected situations
          abroad but can also strengthen your visa application in many cases.
        </p>
      </section>

      {/* WHY NEED SECTION */}
      <section className="kfs-ti-why-section">
        <div className="kfs-ti-container">
          <h2 data-aos="fade-up">
            Why Do You Need International Travel Insurance?
          </h2>
          <p className="kfs-ti-subtitle" data-aos="fade-up">
            Travel comes with surprises—some good, some not-so-great. Travel
            insurance can turn your trip into a great one. It ensures you stay
            covered, stress-free, and financially secure. Here's why it's a
            must-have for every trip!
          </p>

          <div className="kfs-ti-split-content">
            <ul className="kfs-ti-bullet-list" data-aos="fade-right">
              <li>
                <strong>Offers Reimbursement for Flight Delays-</strong> Flight
                delays are frustrating, but what if you got compensation every
                time it happened? With travel insurance, you do!
              </li>
              <li>
                <strong>Gives Compensation for Delayed or Lost Baggage-</strong>{" "}
                Baggage losses are horrible, no doubt, but so are baggage
                delays! If your bags arrive late or are lost, you'll receive
                compensation.
              </li>
              <li>
                <strong>Provides Coverage for Medical Emergencies-</strong> Even
                a minor illness abroad can be shockingly expensive. Your
                insurance takes care of your hefty medical costs.
              </li>
              <li>
                <strong>Ensures a Stress-free Road Trip-</strong> Travel
                insurance covers damage to your rental car and even third-party
                liabilities, so you can enjoy your road trip worry-free.
              </li>
              <li>
                <strong>
                  Assures Protection from Financial Losses Abroad-
                </strong>{" "}
                Missed flights, trip cancellations, or lost belongings can burn
                a hole in your wallet. A policy ensures you don't have to bear
                these losses alone.
              </li>
            </ul>
            <div className="kfs-ti-illustration-box" data-aos="fade-left">
              <img
                src="/images/insurance/tra3.png"
                alt="Travel protection illustration"
              />
            </div>
          </div>
        </div>
      </section>

      {/* STATS SECTION */}
      <section className="kfs-ti-stats-section">
        <div className="kfs-ti-container">
          <h2 className="kfs-ti-centered" data-aos="fade-up">
            Do I Really Need Travel Insurance?
          </h2>
          <p className="kfs-ti-centered kfs-ti-subtitle" data-aos="fade-up">
            If that's a question you've been battling with, read on.
          </p>

          <div className="kfs-ti-stats-grid">
            {[
              {
                icon: "✈️",
                text: "5.80 luggage per 1000 luggage is reportedly mislaid by airlines every year.",
              },
              {
                icon: "🧳",
                text: "1 in 4 passengers have lost their checked-in baggage in the last 3 years.",
              },
              {
                icon: "💊",
                text: "Medical expenses cost 3 to 5 times more outside India.",
              },
              {
                icon: "🎒",
                text: "47% of baggage loss happen during international transfers.",
              },
              {
                icon: "📱",
                text: "Phones, Bank Cards, Licenses, Jewellery, Electronic Gadgets & Passports are the commonly lost belongings while travelling.",
              },
              {
                icon: "⏱️",
                text: "Cancelled flights across the countries ranged from 1.5% to 3.2%.",
              },
              {
                icon: "🚫",
                text: "Trip cancellation, flight cancellations and delays have always been leading causes for travel claims.",
              },
              {
                icon: "🎭",
                text: "Travel scams are extremely common in tourist-heavy countries.",
              },
            ].map((item, index) => (
              <div
                className="kfs-ti-stat-item"
                key={index}
                data-aos="fade-up"
                data-aos-delay={index * 50}
              >
                <span className="kfs-ti-stat-icon">{item.icon}</span>
                <p>{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RIGHT TIME SECTION */}
      <section className="kfs-ti-timing-section">
        <div className="kfs-ti-container kfs-ti-flex-row">
          <div className="kfs-ti-timing-text" data-aos="fade-left">
            <h2>What is the Right Time to Buy Travel Insurance?</h2>
            <p>
              The best time to buy travel insurance is right after booking your
              international trip. This approach ensures coverage for the entire
              duration of your journey, including potential issues that may
              arise before departure, such as flight delays or cancellations.
            </p>
            <p>
              Many travellers delay buying insurance thinking they can do it
              later, but the longer you wait, the more vulnerable you are to
              losing money or facing complications due to uncovered risks.
            </p>
            <p>
              Here are some key reasons why early purchase is highly
              recommended:
            </p>
            <button className="kfs-ti-read-more">Read more</button>
          </div>
        </div>
      </section>

      <section className="kfs-ti-types-section">
        <div className="kfs-ti-container">
          <h2 className="kfs-ti-centered" data-aos="fade-up">
            What are the Types of International Travel Insurance?
          </h2>
          <p
            className="kfs-ti-centered kfs-ti-subtitle kfs-mb-50"
            data-aos="fade-up"
          >
            International travel insurance is categorised based on the purpose
            of your foreign trip, its duration, the type of coverage and your
            needs. Here are some common types:
          </p>

          {[
            {
              title: "International Travel Insurance",
              desc: "An international travel insurance is useful for a foreign trip. In many countries, it is mandatory to have travel insurance with you while applying for your visa. This protects you from unforeseen expenditures.",
              reverse: false,
              src: "/images/insurance/travel2.png",
            },
            {
              title: "Individual Travel Insurance",
              desc: "An individual travel insurance plan is suitable for those who love going on solo trips. Considering the multiple things that can go wrong, it is important to be cautious, especially when travelling alone.",
              reverse: true,
              src: "/images/insurance/travel3.png",
            },
            {
              title: "Student Travel Insurance",
              desc: "If you're a student wanting to go abroad on an educational basis, then this plan is for you. Designed keeping the needs of a student in mind, this student travel insurance plan offers beneficial coverages.",
              reverse: false,
              src: "/images/insurance/travel4.png",
            },
            {
              title: "Group Travel Insurance",
              desc: "This plan benefits the whole group of travellers for unforeseen expenses or losses incurred during the trip. A significant advantage of this plan is reduced costs than individual plans for each traveller.",
              reverse: true,
              src: "/images/insurance/travel5.png",
            },
            {
              title: "Senior Citizen Travel Insurance",
              desc: "Travelling above the age of 60 trails its own risks along. That's why having a travel insurance for senior citizens in hand saves you from unexpected issues like medical expenses, unpredictable financial emergencies, etc.",
              reverse: false,
              src: "/images/insurance/travel6.png",
            },
            {
              title: "Corporate Travel Insurance",
              desc: "The Corporate travel insurance plan is offered to an employee who is travelling on a business trip. This plan is purchased by the firm or employer, to secure the safe travels of the employee.",
              reverse: true,
              src: "/images/insurance/travel7.png",
            },
            {
              title: "Family Travel Insurance",
              desc: "Covering the immediate family members of the policy holder under a single plan, this type of international family travel insurance is designed for Indian families that travel together to foreign countries.",
              reverse: false,
              src: "/images/insurance/travel8.png",
            },
            {
              title: "Schengen Travel Insurance",
              desc: "A Schengen travel insurance is one that is applicable when travelling to the 29 Schengen countries. This plan secures you from financial losses. You can also avail of many benefits as defined by the policy you opt for.",
              reverse: true,
              src: "/images/insurance/travel9.png",
            },
            {
              title: "Annual or Multi-trip Travel Insurance",
              desc: "An annual or multi-trip travel insurance plan is suitable for year-round trips mostly undertaken by those in the corporate sector. If you travel often or more than once in a year, then this plan is suitable for you.",
              reverse: false,
              src: "/images/insurance/travel10.png",
            },
            {
              title: "Single trip Travel Insurance",
              desc: "A single trip travel insurance is one that is applicable when travelling for a single journey. This plan secures you from financial losses for that specific trip.",
              reverse: true,
              src: "/images/insurance/travel11.png",
            },
          ].map((type, index) => (
            <div
              className={`kfs-ti-type-row ${type.reverse ? "kfs-reverse" : ""}`}
              key={index}
              data-aos="fade-up"
              data-aos-delay={index * 100}
            >
              <div className="kfs-ti-type-img">
                <img src={type.src} alt={type.title} />
              </div>
              <div className="kfs-ti-type-text">
                <h3>{type.title}</h3>
                <p>{type.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* COVERAGE SECTION */}
      <section className="kfs-coverage-section">
        <div className="kfs-ti-container">
          <h2 data-aos="fade-up">
            What Does KFS's International Travel Insurance Plan Cover?
          </h2>
          <div className="kfs-coverage-container">
            {coverageData.map((plan, index) => (
              <div
                className="kfs-coverage-card"
                key={plan.id}
                data-aos="flip-left"
                data-aos-delay={index * 200}
              >
                <div className={`kfs-icon-ring ${plan.ringType}`}>
                  <div className="kfs-inner-circle">
                    <span>{plan.icon}</span>
                  </div>
                </div>
                <div className="kfs-coverage-text">
                  <h3>{plan.title}</h3>
                  <p>{plan.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <TestimonialsSection />

      {/* HOW TO BUY SECTION */}
      <section className="kfs-guide-section">
        <div className="kfs-ti-container">
          <h2 className="kfs-section-title" data-aos="fade-up">
            How to Buy International Travel Insurance with KFS?
          </h2>
          <div className="kfs-guide-content">
            <div
              className="kfs-guide-image-wrapper kfs-card-border"
              data-aos="fade-right"
            >
              <img
                src="/images/insurance/tra1.png"
                alt="Travel protection illustration"
              />
            </div>
            <div className="kfs-guide-text-wrapper" data-aos="fade-left">
              <ul className="kfs-vertical-timeline">
                {buySteps.map((step, index) => (
                  <li
                    className="kfs-timeline-item"
                    key={index}
                    data-aos="fade-up"
                    data-aos-delay={index * 100}
                  >
                    <div className="kfs-timeline-dot"></div>
                    <h3>{step.title}</h3>
                    <p>{step.desc}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* HOW TO COMPARE SECTION */}
      <section className="kfs-guide-section kfs-bg-light">
        <div className="kfs-ti-container">
          <h2 className="kfs-section-title kfs-left-align" data-aos="fade-up">
            How to Compare Travel Insurance Policies Online?
          </h2>
          <p className="kfs-section-subtitle kfs-left-align" data-aos="fade-up">
            Finding the right travel insurance is easy when you know what to
            check! Follow these steps to compare quotes like an expert.
          </p>
          <div className="kfs-guide-content kfs-reverse">
            <div
              className="kfs-guide-text-wrapper kfs-list-wrapper"
              data-aos="fade-right"
            >
              <ul className="kfs-bullet-list">
                <li>
                  <strong>Check Medical Coverage:</strong> Medical costs are
                  highly expensive abroad! Look at the medical benefits and sum
                  insured, a.k.a. the amount of coverage, in your travel
                  insurance plan to ensure you're well-covered in case of
                  emergencies.
                </li>
                <li>
                  <strong>Assess the Claim Process:</strong> A good insurance
                  plan should have a quick and hassle-free claim process. Check
                  how claims are settled, the insurer's claim ratio, and whether
                  they offer 24/7 support.
                </li>
                <li>
                  <strong>Check Terms & Choose Smartly:</strong> Go through the
                  terms of claims to know exactly what's covered. Once you've
                  checked all factors, compare plans side by side and pick one
                  that offers the best coverage, smooth claim support, and clear
                  terms.
                </li>
              </ul>
            </div>
            <div className="kfs-guide-image-wrapper" data-aos="fade-left">
              <div className="kfs-placeholder-circle">
                <span className="kfs-rupee-icon">₹</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW TO FILE CLAIM SECTION */}
      <section className="kfs-guide-section">
        <div className="kfs-ti-container">
          <h2 className="kfs-section-title" data-aos="fade-up">
            How to File a Travel Insurance Claim with KFS?
          </h2>
          <p className="kfs-section-subtitle" data-aos="fade-up">
            After you buy our international travel insurance plan, you live
            tension free as we have a 3-step, completely digital claims process!
          </p>
          <div className="kfs-guide-content">
            <div
              className="kfs-guide-image-wrapper kfs-card-border"
              data-aos="fade-right"
            >
              <img
                src="/images/insurance/tra2.png"
                alt="Travel protection illustration"
              />
            </div>
            <div className="kfs-guide-text-wrapper" data-aos="fade-left">
              <ul className="kfs-vertical-timeline">
                {fileSteps.map((step, index) => (
                  <li
                    className="kfs-timeline-item"
                    key={index}
                    data-aos="fade-up"
                    data-aos-delay={index * 100}
                  >
                    <div className="kfs-timeline-dot"></div>
                    <h3>{step.title}</h3>
                    <p>{step.desc}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* TRAVEL VISA SECTION */}
      <TravelVisa />
    </div>
  );
};

export default TravelInsurance;
